import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDownUp, RefreshCw } from 'lucide-react';

import {
  listManagerAudit,
  type ManagerAuditEntry,
  type ManagerAuditPage,
  type ManagerRoleFilter,
} from '@/lib/operatorApi';
import { formatDate, formatDateTime, formatKop } from '@/lib/format';

const ROLE_FILTERS: Array<{ value: ManagerRoleFilter; label: string; countKey: keyof ManagerAuditPage['counts'] }> = [
  { value: 'all', label: 'Все', countKey: 'all' },
  { value: 'director', label: 'Руководители', countKey: 'director' },
  { value: 'head', label: 'Главные менеджеры', countKey: 'head' },
  { value: 'manager', label: 'Менеджеры', countKey: 'manager' },
];

const ACTIONS = [
  { value: '', label: 'Все действия' },
  { value: 'establishment.cash_payment', label: 'Оплата наличными' },
  { value: 'establishment.trial_extend', label: 'Продление триала' },
  { value: 'establishment.days_add', label: 'Дни без оплаты' },
  { value: 'establishment.trial_grace', label: 'Дни пробного от менеджера' },
  { value: 'establishment.update', label: 'Правка заведения' },
  { value: 'establishment.referrer', label: 'Назначение менеджера' },
  { value: 'establishment.delete', label: 'Удаление заведения' },
  { value: 'manager.create', label: 'Новый менеджер' },
  { value: 'manager.update', label: 'Правка сотрудника' },
  { value: 'manager.delete', label: 'Удаление сотрудника' },
  { value: 'manager.login', label: 'Вход в кабинет' },
  { value: 'sales_expense.create', label: 'Расход отдела' },
  { value: 'sales_expense.update', label: 'Правка расхода' },
  { value: 'sales_expense.delete', label: 'Удаление расхода' },
] as const;

const METHOD_LABELS: Record<string, string> = {
  CASH: 'наличные',
  CARD: 'карта',
  TRANSFER: 'перевод',
  SAFE: 'из сейфа',
};

const ROLE_NAMES: Record<string, string> = {
  HEAD_MANAGER: 'главный менеджер',
  SALES_DIRECTOR: 'руководитель',
};

const PAGE = 50;

function str(v: unknown): string | null {
  return typeof v === 'string' && v ? v : null;
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

const PERIOD_LABELS: Record<string, string> = { MONTHLY: 'месяц', YEARLY: 'год' };

const FIELD_LABELS: Record<string, string> = {
  name: 'название',
  address: 'адрес',
  phone: 'телефон',
  ownerName: 'имя владельца',
  ownerPhone: 'телефон владельца',
  email: 'email',
  commissionBps: 'ставка',
};

/** «было → стало» из meta.changes; ставка — в процентах. */
function describeChanges(changes: unknown, member = false): string | null {
  if (!changes || typeof changes !== 'object') return null;
  const parts = Object.entries(changes as Record<string, { from?: unknown; to?: unknown }>).map(([key, c]) => {
    const fmt = (v: unknown) =>
      key === 'commissionBps' && typeof v === 'number' ? `${v / 100} %` : v == null || v === '' ? '—' : String(v);
    const label = key === 'name' && member ? 'имя' : FIELD_LABELS[key] ?? key;
    return `${label}: ${fmt(c?.from)} → ${fmt(c?.to)}`;
  });
  return parts.length ? parts.join(' · ') : null;
}

function personName(v: unknown): string | null {
  const n = (v as { name?: unknown } | null | undefined)?.name;
  return typeof n === 'string' && n ? n : null;
}

/** Что сделал менеджер — по-человечески, с суммой и новым сроком. */
function describe(e: ManagerAuditEntry): { title: string; detail: string | null } {
  const m = e.meta ?? {};
  const after = str(m.after);
  const note = str(m.note);
  const days = num(m.days);
  const tail = (parts: Array<string | null>) => parts.filter(Boolean).join(' · ') || null;
  switch (e.action) {
    case 'establishment.cash_payment': {
      const amount = num(m.amountKopecks);
      const period = str(m.period);
      const until = str(m.until);
      const term = period
        ? PERIOD_LABELS[period] ?? period
        : days
          ? `${days} дн.`
          : until
            ? `до ${formatDate(until)}`
            : null;
      return {
        title: `Принял наличные${amount != null ? ` ${formatKop(amount)}` : ''}`,
        detail: tail([term, after ? `доступ до ${formatDate(after)}` : null, note]),
      };
    }
    case 'establishment.trial_extend':
      return {
        title: `Продлил триал${days ? ` на ${days} дн.` : ''}`,
        detail: tail([after ? `до ${formatDate(after)}` : null]),
      };
    case 'establishment.days_add':
      return {
        title: `Добавил ${days ?? '?'} дн. без оплаты`,
        detail: tail([after ? `оплачено до ${formatDate(after)}` : null, note]),
      };
    case 'manager.create': {
      const role = str(m.role);
      return {
        title: `Добавил ${role === 'HEAD_MANAGER' ? 'главного менеджера' : 'менеджера'} ${str(m.name) ?? ''}`.trim(),
        detail: tail([str(m.email), str(m.code)]),
      };
    }
    case 'manager.login':
      return { title: 'Вход в кабинет', detail: null };
    case 'manager.update':
      return {
        title: `Изменил сотрудника ${str(m.name) ?? ''}`.trim(),
        detail: tail([
          describeChanges(m.changes, true),
          num(m.establishments) ? `ставка обновлена у ${num(m.establishments)} заведений` : null,
        ]),
      };
    case 'manager.delete': {
      const moved = num(m.establishments);
      const to = personName(m.transferTo);
      return {
        title: `Удалил сотрудника ${str(m.name) ?? ''}`.trim(),
        detail: tail([
          str(m.email),
          moved ? (to ? `${moved} заведений → ${to}` : `${moved} заведений без менеджера`) : null,
        ]),
      };
    }
    case 'establishment.trial_grace':
      return {
        title: `Дал ${days ?? '?'} дн. пробного (разово)`,
        detail: tail([after ? `до ${formatDate(after)}` : null]),
      };
    case 'establishment.update':
      return {
        title: 'Изменил профиль заведения',
        detail: tail([describeChanges(m.changes), num(m.networkSize) ? `владелец общий для ${num(m.networkSize)} заведений` : null]),
      };
    case 'establishment.referrer': {
      const from = personName(m.from);
      const to = personName(m.to);
      const bps = num(m.commissionBps);
      return {
        title: to ? `Назначил менеджера ${to}` : 'Снял привязку менеджера',
        detail: tail([from ? `было: ${from}` : 'было: без менеджера', bps != null ? `ставка ${bps / 100} %` : null]),
      };
    }
    case 'establishment.delete':
      return {
        title: 'Удалил заведение (не платило)',
        detail: tail([
          str(m.ownerEmail),
          num(m.orders) != null ? `заказов ${num(m.orders)}` : null,
          num(m.deletedUsers) != null ? `пользователей ${num(m.deletedUsers)}` : null,
        ]),
      };
    case 'sales_expense.create':
    case 'sales_expense.delete': {
      const amount = num(m.amountKopecks);
      const method = str(m.method);
      return {
        title: `${e.action === 'sales_expense.create' ? 'Внёс расход' : 'Удалил расход'}${amount != null ? ` ${formatKop(amount)}` : ''}`,
        detail: tail([method ? METHOD_LABELS[method] ?? method : null, str(m.category)]),
      };
    }
    case 'sales_expense.update':
      return { title: 'Изменил расход отдела', detail: null };
    case 'sales_expense_category.create':
      return { title: `Новая статья расходов «${str(m.name) ?? ''}»`, detail: null };
    case 'sales_expense_category.update':
      return {
        title: `Статья расходов «${str(m.name) ?? ''}»`,
        detail: m.isActive === false ? 'в архиве' : null,
      };
    default:
      return { title: e.action, detail: null };
  }
}

function ObjectLink({ e }: { e: ManagerAuditEntry }) {
  const m = e.meta ?? {};
  if (e.entity === 'Establishment' && e.entityId) {
    // Удалённого заведения в sadmin уже нет — только название из снимка.
    if (e.action === 'establishment.delete') {
      return <span>{str(m.establishmentName) ?? e.entityId.slice(0, 10)}</span>;
    }
    return (
      <Link to={`/tenants/${e.entityId}`} className="hover:text-orange">
        {str(m.establishmentName) ?? e.entityId.slice(0, 10)}
      </Link>
    );
  }
  if (e.entity === 'Manager' && e.entityId) {
    return (
      <Link to={`/managers/${e.entityId}`} className="hover:text-orange">
        {str(m.name) ?? 'менеджер'}
      </Link>
    );
  }
  return <span className="text-ink/35">—</span>;
}

function RoleBadge({ role }: { role: ManagerAuditEntry['actorRole'] }) {
  return ROLE_NAMES[role] ? (
    <span className="badge-orange">{role === 'SALES_DIRECTOR' ? 'руководитель' : 'главный'}</span>
  ) : (
    <span className="badge-ink">менеджер</span>
  );
}

/**
 * Журнал действий менеджеров в их кабинете. Роль фильтруется по снимку на
 * момент действия: что делалось в статусе главного, остаётся в «главных».
 */
export default function ManagerAuditPanel({ managerId }: { managerId?: string }) {
  const [role, setRole] = useState<ManagerRoleFilter>('all');
  const [action, setAction] = useState<string>('');
  const [order, setOrder] = useState<'desc' | 'asc'>('desc');
  const [items, setItems] = useState<ManagerAuditEntry[]>([]);
  const [counts, setCounts] = useState<ManagerAuditPage['counts'] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const query = { role, action: action || undefined, order, managerId, limit: PAGE };

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    listManagerAudit({ role, action: action || undefined, order, managerId, limit: PAGE })
      .then((r) => {
        setItems(r.items);
        setCounts(r.counts);
        setCursor(r.nextCursor);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [role, action, order, managerId]);

  useEffect(() => {
    load();
  }, [load]);

  const loadMore = () => {
    if (!cursor) return;
    setLoadingMore(true);
    listManagerAudit({ ...query, cursor })
      .then((r) => {
        setItems((prev) => [...prev, ...r.items]);
        setCursor(r.nextCursor);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoadingMore(false));
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-1">
          {ROLE_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setRole(f.value)}
              className={
                'border px-3 py-1.5 text-[12px] font-medium transition ' +
                (role === f.value
                  ? 'border-ink bg-ink text-white'
                  : 'border-line bg-white text-ink/70 hover:border-ink/50')
              }
            >
              {f.label}
              {counts ? (
                <span className="ml-1.5 font-mono text-[11px] tabular-nums opacity-60">{counts[f.countKey] ?? 0}</span>
              ) : null}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            className="input !w-auto !py-1.5 text-[13px]"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            aria-label="Действие"
          >
            {ACTIONS.map((a) => (
              <option key={a.value} value={a.value}>
                {a.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn-outline !py-1.5"
            onClick={() => setOrder((o) => (o === 'desc' ? 'asc' : 'desc'))}
          >
            <ArrowDownUp size={13} strokeWidth={1.75} />
            {order === 'desc' ? 'Сначала новые' : 'Сначала старые'}
          </button>
          <button type="button" className="btn-ghost !py-1.5" onClick={load} disabled={loading}>
            <RefreshCw size={13} strokeWidth={1.75} /> Обновить
          </button>
        </div>
      </div>

      {error ? (
        <div className="border border-red-600/30 bg-red-50 px-4 py-3 text-[13px] text-red-700">{error}</div>
      ) : null}

      {/* Desktop */}
      <div className="card hidden md:block">
        <table className="table-grid">
          <thead>
            <tr>
              <th>Менеджер</th>
              <th>Действие</th>
              <th>Заведение / объект</th>
              <th className="text-right">Когда</th>
            </tr>
          </thead>
          <tbody>
            {items.map((e) => {
              const d = describe(e);
              return (
                <tr key={e.id}>
                  <td>
                    {e.manager ? (
                      <Link to={`/managers/${e.manager.id}`} className="font-medium hover:text-orange">
                        {e.manager.name}
                      </Link>
                    ) : (
                      <span className="text-ink/45">удалён</span>
                    )}
                    <div className="mt-1">
                      <RoleBadge role={e.actorRole} />
                    </div>
                  </td>
                  <td>
                    <div className="font-medium text-ink">{d.title}</div>
                    {d.detail ? <div className="mt-0.5 text-[12px] text-ink/55">{d.detail}</div> : null}
                  </td>
                  <td className="text-ink/80">
                    <ObjectLink e={e} />
                  </td>
                  <td className="text-right font-mono text-[12px] text-ink/60">
                    {formatDateTime(e.createdAt)}
                    {e.ip ? <div className="text-[11px] text-ink/40">{e.ip}</div> : null}
                  </td>
                </tr>
              );
            })}
            {!loading && items.length === 0 ? (
              <tr>
                <td colSpan={4} className="py-10 text-center text-ink/50">
                  Действий пока нет
                </td>
              </tr>
            ) : null}
            {loading && items.length === 0 ? (
              <tr>
                <td colSpan={4} className="py-10 text-center text-ink/40">
                  Загрузка…
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {/* Mobile */}
      <ul className="card divide-y divide-line md:hidden">
        {items.map((e) => {
          const d = describe(e);
          return (
            <li key={e.id} className="px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[13px] font-medium text-ink">{d.title}</div>
                  {d.detail ? <div className="mt-0.5 text-[12px] text-ink/55">{d.detail}</div> : null}
                </div>
                <div className="shrink-0 text-right font-mono text-[11px] text-ink/50">
                  {formatDateTime(e.createdAt)}
                </div>
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-ink/60">
                <span className="inline-flex items-center gap-1.5">
                  {e.manager ? (
                    <Link to={`/managers/${e.manager.id}`} className="hover:text-orange">
                      {e.manager.name}
                    </Link>
                  ) : (
                    'удалён'
                  )}
                  <RoleBadge role={e.actorRole} />
                </span>
                <ObjectLink e={e} />
              </div>
            </li>
          );
        })}
        {!loading && items.length === 0 ? (
          <li className="px-4 py-10 text-center text-[13px] text-ink/50">Действий пока нет</li>
        ) : null}
        {loading && items.length === 0 ? (
          <li className="px-4 py-10 text-center text-[13px] text-ink/40">Загрузка…</li>
        ) : null}
      </ul>

      {cursor ? (
        <div className="flex justify-center">
          <button type="button" className="btn-outline" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? 'Загрузка…' : 'Показать ещё'}
          </button>
        </div>
      ) : null}
    </div>
  );
}
